// import * as SetUtil from "@greeter/set";

/**
 * Mutates the map with key and array as value.
 *
 * Finds the key and the value then adds to the array.
 * If it doesnt exist, create a new array and add to the array.
 */
export function upsert<TKey, TValue>(
  map: Map<TKey, Array<TValue>>,
  k: TKey,
  v: TValue
) {
  const existing = map.get(k);

  if (existing) {
    existing.push(v);
  } else {
    map.set(k, [v]);
  }

  return map;
}

export function getId(e: { id: string }) {
  return e.id;
}

/**
 * Indexes an array by some property into a map.
 *
 * Make sure the key is unique as the next value will overrride the previous value.
 */
export function indexBy<T, TKey>(
  arr: Array<T>,
  by: (input: T) => TKey
): Map<TKey, T> {
  const m = new Map<TKey, any>();

  for (let i = 0, l = arr.length; i < l; i++) {
    const item = arr[i];
    m.set(by(item), item);
  }

  return m;
}

export function indexById<T extends { id: TKey }, TKey extends string>(
  arr: Array<T>
) {
  return indexBy(arr, (i) => i.id as TKey);
}

/**
 * Creates a lookup table for arrays
 */
export function lookupBy<T extends Array<any>, TKey>(
  arr: T,
  by: (input: T[0]) => TKey
): Map<TKey, T> {
  const m = new Map<TKey, T>();

  for (let i = 0, l = arr.length; i < l; i++) {
    const item = arr[i];
    upsert(m, by(item), item);
  }

  return m;
}

function _intersection<T>(a: Set<T>, b: Set<T>) {
  const result = new Set<T>();

  for (const item of a) {
    if (b.has(item)) {
      result.add(item);
    }
  }

  for (const item of b) {
    if (a.has(item)) {
      result.add(item);
    }
  }

  return result;
}

// TODO: How do typing?
export function intersectingKeys<TKey>(
  a: Map<TKey, any>,
  b: Map<TKey, any>
): Set<TKey> {
  const aKeys = new Set(a.keys());
  const bKeys = new Set(b.keys());
  return _intersection(aKeys, bKeys);
}

/**
 * Merges two maps. Beware, the second map overrides the first map.
 */
export function merge<TKey, TValue>(
  m1: Map<TKey, TValue>,
  m2: Map<TKey, TValue>
) {
  const r = new Map<TKey, TValue>();

  for (const [k, v] of m1.entries()) {
    r.set(k, v);
  }

  for (const [k, v] of m2.entries()) {
    r.set(k, v);
  }

  return r;
}
